import { useEffect, useState } from "react";
import { api } from "@/shared/api";
import { setAppConfigId, wecomApi } from "./wecomApi";
import { errorText } from "@/shared/lib/apiErrors";
import { isWeComBrowser } from "./wecomEnv";

/**
 * 企微 H5 鉴权 Hook(2026-06-12 cookie cutover)。
 *
 * rebuild backend 的 OAuth 回调把 MEMBER JWT 写 HttpOnly cookie(P0:token 不入 JS/URL,
 * 回调只带 `#login=ok` / `#error=msg` 信号),JS 不再持有 token —— 旧 `#token=` fragment +
 * localStorage 模式随 legacy 退役。
 *
 * 逻辑:
 * 1. hash 带 `#error=msg` → 显示错误。
 * 2. hash 带 `#login=ok` → OAuth 刚回来,cookie 已种,authed=true(清 hash)。
 * 3. 都没有 → 探活(my-home,cookie 随同域请求自动携带):
 *    - 2xx → authed=true(cookie 仍有效,刷新/二次进入)
 *    - 401 → wecomApi 自动发起 OAuth 跳转(需 appConfigId)
 */
/**
 * 租户链身份(老板/经理/员工走企微 OAuth 拿 TENANT token)不属于本 MEMBER 入口:
 * my-home 等 /wecom-h5/* 端点对他们必 403,原流程会反复重走 OAuth 或直接「授权失败」。
 * 用 /advisor/identity(authenticated 级,租户链可达、MEMBER 403)甄别,命中即整页
 * 送去军师 H5(他们真正的家),返回 true 表示已发起跳转(2026-07-11 走查修)。
 */
/**
 * 租户链身份分流到 advisor-h5 的落点(2026-07-13,肖小波/T-69BAC611 实锤):
 * 旧版硬编码 #/advisor(军师首页),陪练通知卡带的 taskId/scenarioId 全丢 →「通知点不进去」。
 * 现在 query 带陪练参数就带深链直达对练页(顺带修好存量已发出的旧通知卡);否则仍落首页。
 */
export function advisorLandingUrl(search: string): string {
  try {
    const p = new URLSearchParams(search);
    const taskId = p.get("taskId");
    const scenarioId = p.get("scenarioId");
    if (taskId && scenarioId) {
      return (
        "/advisor-h5.html#/advisor/coaching/chat?scenarioId=" +
        encodeURIComponent(scenarioId) +
        "&taskId=" +
        encodeURIComponent(taskId)
      );
    }
  } catch {
    /* 参数解析失败回落首页 */
  }
  return "/advisor-h5.html#/advisor";
}

async function redirectToAdvisorIfTenantChain(): Promise<boolean> {
  try {
    const res = await fetch("/api/v1/advisor/identity", {
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) return false; // 403=MEMBER/匿名 → 走原 MEMBER 流程
    const body = (await res.json().catch(() => null)) as { data?: { role?: string } } | null;
    const role = body?.data?.role;
    if (role && role !== "MEMBER") {
      window.location.replace(advisorLandingUrl(window.location.search));
      return true;
    }
  } catch {
    // 网络错 → 回落原流程,由原探活给出错误提示
  }
  return false;
}

// 1005:非企微环境判断搬到 wecomEnv(advisor-h5 也要用),这里转出口保持旧引用不变。
export { isWeComBrowser };

export function useWeComAuth(appConfigId?: string) {
  const [authed, setAuthed] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notInWeCom, setNotInWeCom] = useState(false);

  useEffect(() => {
    if (!isWeComBrowser()) {
      setNotInWeCom(true);
      setReady(true);
      return;
    }

    // 持久化 appConfigId,供 401 自动续签时复原
    if (appConfigId) setAppConfigId(appConfigId);

    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const hashError = hashParams.get("error");
    const loginOk = hashParams.get("login") === "ok";

    if (hashError) {
      // 全中文化(Batch 0.5):OAuth 回调 error 可能是后端/企微英文原文,含英文时兜通用中文
      const decoded = decodeURIComponent(hashError);
      setError(/[A-Za-z]{3,}/.test(decoded) ? "登录失败,请从企微入口重新进入" : decoded);
      setReady(true);
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      return;
    }

    if (loginOk) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      // OAuth 刚回来:租户链身份(TENANT token)在本 MEMBER 入口寸步难行,先甄别分流
      let cancelledLogin = false;
      void redirectToAdvisorIfTenantChain().then((redirected) => {
        if (cancelledLogin || redirected) return;
        setAuthed(true);
        setReady(true);
      });
      return () => {
        cancelledLogin = true;
      };
    }

    // 无登录信号:先甄别租户链身份(命中即跳军师 H5),再探活 MEMBER cookie
    // (HttpOnly,JS 不可读,只能靠请求结果判断)。
    // 401 时 wecomApi 内部自动发起 OAuth 跳转(页面将被替换);其余错误展示。
    let cancelled = false;
    redirectToAdvisorIfTenantChain()
      .then((redirected) => {
        if (cancelled || redirected) return Promise.reject(new Error("__redirected__"));
        return wecomApi.get("/wecom-h5/coaching/my-home");
      })
      .then(() => {
        if (cancelled) return;
        setAuthed(true);
        setReady(true);
      })
      .catch(async (e: Error) => {
        if (cancelled) return;
        // 租户链已整页跳军师 H5:保持 loading 等页面替换,别再走 OAuth
        if (e.message === "__redirected__") return;
        // UnauthorizedError = 正在跳 OAuth(或缺 appConfigId);其余为真错误
        if (e.name === "UnauthorizedError") {
          if (!appConfigId) {
            setError("登录已过期且缺少 appConfigId 参数,请从企微入口重新进入");
            setReady(true);
          }
          // 有 appConfigId 时 wecomApi 已发起跳转,保持 loading 等待页面替换
          return;
        }
        // 探活业务错误也尝试直接走 OAuth(首次进入无 cookie 的正常路径)
        if (appConfigId) {
          try {
            const url = await api.get<string>(`/wecom/oauth/url?appConfigId=${appConfigId}`);
            window.location.href = url;
            return;
          } catch (oauthErr) {
            setError(errorText(oauthErr, { fallbackTitle: "授权跳转失败" }));
            setReady(true);
            return;
          }
        }
        setError(errorText(e, { fallbackTitle: "登录状态校验失败" }));
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [appConfigId]);

  const logout = () => {
    // cookie 是 HttpOnly,JS 清不掉;登出语义=丢弃本地态,下次进入由探活/OAuth 重建
    setAuthed(false);
    setReady(false);
  };

  return { authed, ready, error, notInWeCom, logout };
}
